/**
 * Public SupportPage — accessible without login at `/support`.
 * This is the URL Apple App Store Connect and Google Play Console point
 * at as the "Support URL" for the PrepMasters app listing.
 *
 * Content:
 *   • Contact block (email / phone / Facebook / address) — pulled from
 *     the same `/api/landing` doc the footer uses so a super-admin can
 *     edit it in Settings → Landing Page without touching code.
 *   • FAQ accordion covering account, payments, IAP, refunds, features.
 *   • Links to the Privacy Policy and Terms of Use.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import BrandLogo from "@/components/BrandLogo";
import {
  Mail, Phone, Facebook, MapPin, ShieldCheck, Scale, LifeBuoy, ChevronLeft,
} from "lucide-react";

// FAQ content is hardcoded here on purpose — Apple reviewers want a
// stable, always-available support page. A super-admin can still edit
// the contact block via Settings → Landing Page.
const FAQ_SECTIONS = [
  {
    heading: "Getting started",
    items: [
      {
        q: "How do I create a PrepMasters account?",
        a: "Tap Register on the landing page, enter your email and a password, tick the checkbox agreeing to our Privacy Policy and Terms of Use, then verify your email through the confirmation link we send. You can also sign in with Google or Apple — the terms checkbox appears above those buttons too. Once approved, you can log in and pick the course you're preparing for.",
      },
      {
        q: "I registered but I can't log in yet — what's happening?",
        a: "One of two things: (1) You haven't clicked the email verification link we sent to your inbox — check spam too, and if the link expired, request a new one from the login screen. (2) Your admin has 'Auto-approve new registrations' turned off, so your account is pending manual approval. You'll get an email once you're approved, usually within one working day.",
      },
      {
        q: "How do I reset my password?",
        a: "On the login screen, tap 'Forgot password?', enter your email, and we'll send you a reset link. The link is valid for one hour. If you signed up with Google or Apple, you don't have a PrepMasters password — just tap Continue with Google / Continue with Apple to log in.",
      },
      {
        q: "Why does PrepMasters only let me log in on one device at a time?",
        a: "To protect the paid content and your account from unauthorised sharing, PrepMasters enforces a one-device-at-a-time policy. Logging in on a new device automatically signs you out of the old one — no action needed.",
      },
    ],
  },
  {
    heading: "Courses, subjects & study modes",
    items: [
      {
        q: "What courses does PrepMasters cover?",
        a: "PrepMasters covers Philippine board and civil-service examinations, including the Licensure Examination for Teachers (LET), Nursing Licensure Examination (NLE), Civil Service Examination (Professional & Sub-Professional), Real Estate Brokers Licensure Exam (REBLE), and more. Available courses are shown on the landing page's 'Available Courses' section.",
      },
      {
        q: "What's the difference between Exam Mode, Review Mode, and Podcast Mode?",
        a: "Exam Mode gives you a timed test and tracks your score. Review Mode is a study-first flow that shows the correct answer and an AI-generated rationale after each question so you learn as you go. Podcast Mode reads the question, choices, and answer aloud with configurable pauses between sections — perfect for hands-free review during a commute.",
      },
      {
        q: "How does Smart Review (spaced repetition) work?",
        a: "PrepMasters uses the FSRS algorithm — a modern spaced-repetition system — to surface your weakest topics and the questions you've missed most recently. Each day you'll see a Due count in the Study menu; work through it and the algorithm rescheduling ensures every minute of study is spent where it matters most.",
      },
      {
        q: "Do I lose my progress if I lose my internet connection?",
        a: "No. PrepMasters is offline-first for cached Smart-Review sets — you can practise on the MRT, jeepney, or anywhere with no Wi-Fi, and your answers sync when you reconnect. The Podcast Mode audio is also cached on our servers for instant replay.",
      },
    ],
  },
  {
    heading: "Payments, subscriptions & refunds",
    items: [
      {
        q: "How much does PrepMasters cost?",
        a: "Prices vary per course and are shown in Philippine Pesos (₱) at the time of purchase. Each purchase gives you a fixed access period (typically 6 months) for that course. There are 4 pricing tiers depending on the course: ₱499, ₱999, ₱2,490, and ₱4,990. The exact price for each course is displayed on the enrolment screen.",
      },
      {
        q: "How do I pay?",
        a: "In the iOS app, purchases go through Apple In-App Purchase (billed to your Apple ID). In the Android app, purchases go through Google Play Billing (billed to your Google account). On the web, you can pay via GCash, GoTyme Bank, PayMongo, or bank transfer — upload the proof of payment and an admin will verify it within one working day.",
      },
      {
        q: "Is my subscription auto-renewing?",
        a: "No. Course access is NOT auto-renewing. When your access period ends, the course locks and you can purchase a new access period to continue. You will never be charged again without explicitly tapping 'Subscribe' or 'Buy' inside the app.",
      },
      {
        q: "How do I get a refund?",
        a: "Refunds depend on how you paid: (1) Apple in-app purchase — request a refund via https://reportaproblem.apple.com, Apple is the merchant of record. (2) Google Play purchase — request a refund via Google Play within 48 hours, or contact us afterwards. (3) Manual bank / e-wallet payment — email support@prepmasters.ph, refunds are offered for duplicate payments, prolonged service outages, or mistaken enrolment reported within 24 hours (see our Terms of Use for full details).",
      },
      {
        q: "I paid via GCash / bank but my account is still locked.",
        a: "Manual payments require admin verification. Please check that (a) you uploaded a clear photo of your proof of payment on the Payment Instructions screen and (b) the reference number matches. Verifications usually complete within one working day. If it's been longer than 24 hours, email support@prepmasters.ph with your registered email and the reference number.",
      },
    ],
  },
  {
    heading: "Data privacy & account management",
    items: [
      {
        q: "How can I delete my account and all my data?",
        a: "Log into the app, go to Account → Delete Account (or email support@prepmasters.ph from your registered email address). We erase your personal information and learning data. Payment records are retained for 7 years to comply with Bureau of Internal Revenue (BIR) rules, as explained in our Privacy Policy.",
      },
      {
        q: "Where is my data stored?",
        a: "Your account and learning data live on our own servers hosted in the Philippines (Vultr) plus MongoDB Atlas. Some data is transferred to third-party processors (AI providers, Firebase, Resend) as described in our Privacy Policy. All traffic is encrypted with HTTPS/TLS.",
      },
      {
        q: "Do you sell my personal information?",
        a: "No. PrepMasters never sells your personal information. We share data only with the payment, authentication, AI, email, and hosting providers we need to operate the app — see Section 5 of our Privacy Policy for the full list.",
      },
    ],
  },
  {
    heading: "Technical issues",
    items: [
      {
        q: "The Podcast Mode audio isn't loading.",
        a: "Podcast Mode caches audio on our server the first time it's generated. If audio doesn't start, try (a) tapping Play once more, (b) checking your internet connection, (c) killing and reopening the app. If the issue persists, email support@prepmasters.ph with your course and module name.",
      },
      {
        q: "I keep getting 'Verification failed. Please refresh and try again.'",
        a: "This means our Cloudflare Turnstile anti-bot widget didn't finish. Refresh the page (or restart the app), wait a couple of seconds for the widget to load, and try again. If it happens repeatedly on the web, disable browser extensions that block trackers and try in an incognito window.",
      },
      {
        q: "Push notifications aren't arriving on my phone.",
        a: "Check that (a) you granted the app notification permission in your phone's system settings, (b) PrepMasters is not on your Do Not Disturb whitelist, and (c) you're logged in. You can also turn notifications on/off from Account → Notifications inside the app.",
      },
    ],
  },
];


export default function SupportPage() {
  const [lp, setLp] = useState(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/landing`)
      .then((r) => { if (alive) setLp((r.data && r.data.landing) || {}); })
      .catch(() => { if (alive) setLp({}); });
    return () => { alive = false; };
  }, []);

  const contactEmail = lp?.contact_email || "support@prepmasters.ph";
  const contactPhone = lp?.contact_number || "+63 900 000 0000";
  const facebookLink = lp?.facebook_link || "https://facebook.com/prepmasters";
  const address = lp?.address || "Manila, Philippines";

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100" data-testid="support-page">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2" data-testid="support-home-link">
            <BrandLogo size="sm" />
          </Link>
          <Button variant="ghost" size="sm" asChild data-testid="support-back-btn">
            <Link to="/"><ChevronLeft className="w-4 h-4 mr-1" /> Back to Home</Link>
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-10">
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-600">
            <LifeBuoy className="w-4 h-4" /> Help &amp; Support
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-2">
            How can we help?
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2 max-w-2xl">
            Browse the FAQ below, or get in touch — we usually reply within one working day.
          </p>
        </div>

        {/* Contact card */}
        <Card className="p-6 border-slate-200 dark:border-slate-800" data-testid="support-contact-card">
          <h2 className="font-display text-lg font-bold">Contact PrepMasters</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
            <a href={`mailto:${contactEmail}`} className="flex items-center gap-3 p-3 rounded-md border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid="support-email">
              <Mail className="w-4 h-4 text-blue-700" />
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500">Email</div>
                <div className="font-semibold">{contactEmail}</div>
              </div>
            </a>
            <a href={`tel:${contactPhone.replace(/\s+/g, "")}`} className="flex items-center gap-3 p-3 rounded-md border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid="support-phone">
              <Phone className="w-4 h-4 text-blue-700" />
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500">Phone</div>
                <div className="font-semibold">{contactPhone}</div>
              </div>
            </a>
            <a href={facebookLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 rounded-md border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid="support-facebook">
              <Facebook className="w-4 h-4 text-blue-700" />
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500">Facebook</div>
                <div className="font-semibold">Message us on Facebook</div>
              </div>
            </a>
            <div className="flex items-center gap-3 p-3 rounded-md border border-slate-200 dark:border-slate-700">
              <MapPin className="w-4 h-4 text-blue-700" />
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500">Address</div>
                <div className="font-semibold">{address}</div>
              </div>
            </div>
          </div>
        </Card>

        {/* FAQ */}
        <div>
          <h2 className="font-display text-2xl font-bold" data-testid="support-faq-heading">Frequently Asked Questions</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Answers to the questions we hear most often.</p>

          <div className="mt-6 space-y-6">
            {FAQ_SECTIONS.map((section, si) => (
              <Card key={si} className="p-6 border-slate-200 dark:border-slate-800" data-testid={`faq-section-${si}`}>
                <h3 className="font-display text-base font-bold text-slate-900 dark:text-slate-100">{section.heading}</h3>
                <Accordion type="single" collapsible className="mt-3">
                  {section.items.map((it, ii) => (
                    <AccordionItem key={ii} value={`s${si}-i${ii}`} data-testid={`faq-item-${si}-${ii}`}>
                      <AccordionTrigger className="text-sm text-left">{it.q}</AccordionTrigger>
                      <AccordionContent className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{it.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Card>
            ))}
          </div>
        </div>

        {/* Legal shortcut */}
        <Card className="p-6 border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-900/40" data-testid="support-legal-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-display text-base font-bold">Still need help?</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                Email <a className="text-blue-700 font-semibold" href={`mailto:${contactEmail}`}>{contactEmail}</a> — we reply within one working day.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" asChild data-testid="support-privacy-link">
                <Link to="/legal/privacy"><ShieldCheck className="w-4 h-4 mr-1" /> Privacy Policy</Link>
              </Button>
              <Button variant="outline" size="sm" asChild data-testid="support-terms-link">
                <Link to="/legal/terms"><Scale className="w-4 h-4 mr-1" /> Terms of Use</Link>
              </Button>
            </div>
          </div>
        </Card>

        <div className="text-center text-xs text-slate-500 pt-4">
          © {new Date().getFullYear()} PrepMasters. All rights reserved.
        </div>
      </main>
    </div>
  );
}
